import { useCallback, useMemo } from 'react';
import { getSegmentAtCurrentFrame, getSegmentTimeFromPlayerTime } from '../../timeUtils';
import { canAddScene } from '../../utils';
import { AddMenuItem } from '../../TimelinePlayerActionBar/AddMenu';
import { updateClipData } from '../../clipUpdateDataUtils';
import { CLIP_UPDATE_TYPE } from '../../clipUpdateDataUtils/constants';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { useRemotionPlayerCurrentTime } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerCurrentTime';
import { transcriptTimelineSelectionStore } from '@/stores/transcriptTimelineSelection';
import { SegmentType } from '@/player-config/src/types/Segment/Type';

/**
 * Hook that provides functionality to add new scenes at a specific frame position.
 * Handles both initial scene creation and splitting existing scenes.
 */
export const useAddScene = () => {
  const { clipId, clipData, fps } = useClipsContext();
  const { currentTime } = useRemotionPlayerCurrentTime();

  /**
   * Adds a new scene at the specified frame position.
   * When no scenes exist, creates two scenes:
   * - First scene from start to current position
   * - Second scene from current position to end
   * When scenes exist, splits the current scene at the specified position.
   *
   * @param currentFrame - The frame position where the new scene should be added
   * @param compositeSegment - The composite segment containing the video content
   */
  const addScene = useCallback(
    (currentFrame: number, compositeId: string) => {
      updateClipData({
        actionType: CLIP_UPDATE_TYPE.ADD_COMPOSITE_SCENE,
        currentFrame,
        compositeId,
        clipId
      });
    },
    [clipId]
  );

  const addSceneForRange = useCallback(
    (startTimeFrame: number, endTimeFrame: number, compositeId: string) => {
      updateClipData({
        actionType: CLIP_UPDATE_TYPE.ADD_COMPOSITE_SCENE_RANGE,
        compositeId,
        startFrame: startTimeFrame,
        endFrame: endTimeFrame,
        clipId
      });
    },
    [clipId]
  );

  /**
   * Handles adding a scene at the current playback position.
   * When no scenes exist, it creates two scenes:
   * - First scene from start to current time
   * - Second scene from current time to end
   * When scenes exist, it splits the current scene at the playback position.
   */
  const handleAddScene = useCallback(async () => {
    const { startTime, endTime, endSegmentId, startSegmentId } = transcriptTimelineSelectionStore.getSnapshot();

    if (startTime !== null && endTime !== null) {
      if (startSegmentId === endSegmentId && startSegmentId) {
        addSceneForRange(startTime * fps, endTime * fps, startSegmentId);
      }
    } else {
      const currentSegment = getSegmentAtCurrentFrame(currentTime, clipData);
      if (currentSegment?.type !== SegmentType.COMPOSITE) {
        return;
      }

      const segmentTime = getSegmentTimeFromPlayerTime(currentTime, clipData, currentSegment.id);
      addScene(segmentTime * fps, currentSegment.id);
    }
  }, [clipData, addSceneForRange, currentTime, addScene, fps]);

  const isMenuItemDisabled = useMemo(() => {
    const currentSegment = getSegmentAtCurrentFrame(currentTime, clipData);
    const isCurrentSegmentComposite = currentSegment?.type === SegmentType.COMPOSITE;
    const compositeSegment = isCurrentSegmentComposite ? currentSegment : null;

    return (
      !compositeSegment ||
      !canAddScene({
        compositeSegment,
        currentTime,
        clipData
      })
    );
  }, [clipData, currentTime]);

  const addSceneMenuItem = useMemo(
    (): AddMenuItem | null => ({
      id: 'add-scene',
      label: 'Scene change',
      iconName: 'IconLayoutColumns' as const,
      onClick: handleAddScene,
      isDisabled: isMenuItemDisabled
    }),
    [handleAddScene, isMenuItemDisabled]
  );

  return { addScene, addSceneForRange, addSceneMenuItem };
};
